// ============================================================
// ToastContext - lightweight notification system.
//
// pushToast(message, { type, duration, action }) shows a transient toast;
// errors surface sync failures that previously died in console.error
// (Phase 4 of the 2026-06 overhaul). `action: { label, onClick }` adds a
// button - the asset library's Undo (v11.68.0) - and a toast with one
// holds still while it is pointed at or focused, so the button cannot
// vanish under the hand reaching for it. No dependency.
// ============================================================
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Toast from '../components/Toast';

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  const durationsRef = useRef(new Map());

  const holdToast = useCallback((id) => {
    clearTimeout(timersRef.current.get(id));
    timersRef.current.delete(id);
  }, []);

  const dismissToast = useCallback(
    (id) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      holdToast(id);
      durationsRef.current.delete(id);
    },
    [holdToast],
  );

  /** (Re)start a toast's countdown from its whole duration; a toast with none stays until dismissed. */
  const releaseToast = useCallback(
    (id) => {
      const duration = durationsRef.current.get(id);
      if (!duration) return;
      holdToast(id);
      timersRef.current.set(
        id,
        setTimeout(() => dismissToast(id), duration),
      );
    },
    [dismissToast, holdToast],
  );

  const pushToast = useCallback(
    (message, { type = 'info', duration = 5000, action = null } = {}) => {
      const id = nextId++;
      setToasts((prev) => [...prev.slice(-4), { id, message, type, action }]); // cap at 5 visible
      if (duration > 0) durationsRef.current.set(id, duration);
      releaseToast(id);
      return id;
    },
    [releaseToast],
  );

  const value = useMemo(() => ({ pushToast, dismissToast }), [pushToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast toasts={toasts} onDismiss={dismissToast} onHold={holdToast} onRelease={releaseToast} />
    </ToastContext.Provider>
  );
}

/** Returns { pushToast(message, {type:'info'|'error'|'success', duration, action:{label,onClick}}), dismissToast(id) }. */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
